package com.eladfinish.bible.contest.ui.components.toolbar.autohide

import androidx.compose.ui.util.lerp
import kotlin.math.abs

/** Where the scrollable content of the active page is, in pixels. */
internal data class ToolbarAutoHideScrollMetrics(
    val offset: Float,
    val maxOffset: Float,
)

internal enum class ToolbarAutoHideEasing { HIDE, SHOW, SHOW_NEAR_TOP }

internal data class ToolbarAutoHideTransition(
    val isHidden: Boolean,
    val durationMs: Int,
    val easing: ToolbarAutoHideEasing,
)

/**
 * Decides when the app's toolbar hides and comes back while the content scrolls. A port of what Chrome
 * for Android does with its own toolbar (Chromium: cc/input/browser_controls_snap_animation_controller.cc
 * and browser_controls_offset_manager.cc), with its numbers. Positive deltas scroll towards the end.
 *
 * It only hides beyond 1.5 toolbar heights from the top, so a chapter that overflows the screen by less
 * never hides the toolbar: the screen grows by the toolbar's height when it hides, and that chapter could
 * then fit, leaving no scroll to bring the toolbar back.
 */
internal class ToolbarAutoHideLogic(
    private val triggerReferencePx: Float,
) {
    private class Sample(val timeMs: Long, val delta: Float)

    private val samples = ArrayDeque<Sample>()
    private var accumulatedDelta = 0f
    private var didHideThisScroll = false
    private var animationEndsAtMs = Long.MIN_VALUE

    /** The toolbar is one, screens come and go: each screen's logic starts from what the toolbar really shows. */
    var isHidden = false

    fun onScrollBegin(nowMs: Long) {
        accumulatedDelta = 0f
        samples.clear()
        if (isAnimating(nowMs)) didHideThisScroll = isHidden
    }

    fun onScrollUpdate(
        delta: Float,
        isInertial: Boolean,
        nowMs: Long,
        metrics: ToolbarAutoHideScrollMetrics,
        toolbarHeightPx: Float,
    ): ToolbarAutoHideTransition? {
        // At the end of the content a scroll towards the end moves nothing
        if (delta > 0f && metrics.offset >= metrics.maxOffset) return null

        accumulatedDelta += delta

        val velocity = velocity()
        val isSameDirection = (velocity >= 0f && delta >= 0f) || (velocity <= 0f && delta <= 0f)
        if (!isSameDirection) samples.clear()
        samples.addLast(Sample(nowMs, delta))
        dropSamplesOlderThanWindow(nowMs)

        // A single sample gives no accurate velocity
        if (samples.size < 2) return null

        return apply(
            pendingDelta = delta,
            isScrollEndOrInertial = isInertial,
            nowMs = nowMs,
            metrics = metrics,
            toolbarHeightPx = toolbarHeightPx,
        )
    }

    /** The finger was lifted, or a fling or a mouse wheel's scroll has ended. */
    fun onScrollEnd(
        nowMs: Long,
        metrics: ToolbarAutoHideScrollMetrics,
        toolbarHeightPx: Float,
    ): ToolbarAutoHideTransition? {
        dropSamplesOlderThanWindow(nowMs)
        val transition = apply(
            pendingDelta = 0f,
            isScrollEndOrInertial = true,
            nowMs = nowMs,
            metrics = metrics,
            toolbarHeightPx = toolbarHeightPx,
        )
        samples.clear()
        didHideThisScroll = false
        return transition
    }

    /** Back to shown, whatever the scroll is doing: a new chapter, or content with nothing left to scroll. */
    fun forceShow(nowMs: Long): ToolbarAutoHideTransition? {
        if (!isHidden) return null
        return start(isHidden = false, durationMs = MAX_DURATION_MS, easing = ToolbarAutoHideEasing.SHOW_NEAR_TOP, nowMs = nowMs)
    }

    private fun apply(
        pendingDelta: Float,
        isScrollEndOrInertial: Boolean,
        nowMs: Long,
        metrics: ToolbarAutoHideScrollMetrics,
        toolbarHeightPx: Float,
    ): ToolbarAutoHideTransition? {
        if (toolbarHeightPx <= 0f || isAnimating(nowMs)) return null

        val slowness = slowness(toolbarHeightPx)
        val duration = lerp(MIN_DURATION_MS.toFloat(), MAX_DURATION_MS.toFloat(), slowness).toInt()
        val threshold = triggerReferencePx * lerp(TRIGGER_MIN_MULTIPLIER, 1f, slowness)

        if (velocity() >= 0f) {
            if (isHidden) return null
            if (didHideThisScroll && !isScrollEndOrInertial) return null
            val canHideMultiplier = if (isScrollEndOrInertial) {
                CAN_HIDE_MIN_MULTIPLIER
            } else {
                lerp(CAN_HIDE_MIN_MULTIPLIER, CAN_HIDE_MAX_MULTIPLIER, slowness)
            }
            val isInCanHideRegion = metrics.offset > toolbarHeightPx * canHideMultiplier &&
                    metrics.offset < metrics.maxOffset
            if (!isInCanHideRegion || accumulatedDelta < threshold) return null

            didHideThisScroll = true
            return start(isHidden = true, durationMs = duration, easing = ToolbarAutoHideEasing.HIDE, nowMs = nowMs)
        }

        if (!isHidden) return null
        val isInAlwaysShownRegion = metrics.offset + pendingDelta <= toolbarHeightPx * ALWAYS_SHOWN_MULTIPLIER
        if (!isInAlwaysShownRegion && accumulatedDelta > -threshold) return null

        val easing = if (metrics.offset > toolbarHeightPx) ToolbarAutoHideEasing.SHOW else ToolbarAutoHideEasing.SHOW_NEAR_TOP
        return start(isHidden = false, durationMs = duration, easing = easing, nowMs = nowMs)
    }

    private fun start(
        isHidden: Boolean,
        durationMs: Int,
        easing: ToolbarAutoHideEasing,
        nowMs: Long,
    ): ToolbarAutoHideTransition {
        this.isHidden = isHidden
        animationEndsAtMs = nowMs + durationMs
        return ToolbarAutoHideTransition(isHidden = isHidden, durationMs = durationMs, easing = easing)
    }

    private fun isAnimating(nowMs: Long): Boolean = nowMs < animationEndsAtMs

    private fun dropSamplesOlderThanWindow(nowMs: Long) {
        while (samples.isNotEmpty() && nowMs - samples.first().timeMs > MAX_DURATION_MS) {
            samples.removeFirst()
        }
    }

    /** In pixels per millisecond. */
    private fun velocity(): Float {
        if (samples.size < 2) return 0f
        val elapsedMs = samples.last().timeMs - samples.first().timeMs
        if (elapsedMs <= 0L) return 0f
        return samples.drop(1).sumOf { it.delta.toDouble() }.toFloat() / elapsedMs
    }

    /** 0 for a fast scroll, 1 for a slow one: how long the toolbar's height takes at this speed. */
    private fun slowness(toolbarHeightPx: Float): Float {
        val speed = abs(velocity())
        if (speed == 0f) return 1f
        return (toolbarHeightPx / speed / MAX_DURATION_MS).coerceIn(0f, 1f)
    }

    private companion object {
        const val MIN_DURATION_MS = 75
        const val MAX_DURATION_MS = 200
        const val ALWAYS_SHOWN_MULTIPLIER = 0.5f
        const val CAN_HIDE_MIN_MULTIPLIER = 1.5f
        const val CAN_HIDE_MAX_MULTIPLIER = 3f
        const val TRIGGER_MIN_MULTIPLIER = 0.2f
    }
}
